import { useEffect, useState } from 'react'
import { useAuth } from '../auth'
import {
  grantAdminResources,
  listAdminUsers,
  type AdminUserSummary,
  type ResourceGrant,
} from '../services/api'

function formatDate(value: string | null) {
  if (!value) return 'No saved progress yet'
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
}

export function AdminDashboard() {
  const { user } = useAuth()
  const [users, setUsers] = useState<AdminUserSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [busyUserId, setBusyUserId] = useState('')
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  async function refresh() {
    setLoading(true)
    setError('')
    try {
      setUsers(await listAdminUsers())
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load users.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (user.isAdmin) void refresh()
  }, [user.isAdmin])

  async function grant(userId: string, resources: ResourceGrant, label: string) {
    setBusyUserId(userId)
    setMessage('')
    setError('')
    try {
      const updated = await grantAdminResources(userId, resources)
      setUsers((current) => current.map((entry) => entry.id === updated.id ? updated : entry))
      setMessage(`${label} granted to ${updated.email}. Ask that user to refresh ThriveCove before continuing the test.`)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not grant resources.')
    } finally {
      setBusyUserId('')
    }
  }

  if (!user.isAdmin) return null

  return (
    <section className="panel settings-section admin-section">
      <div className="admin-heading">
        <div>
          <p className="eyebrow">Testing controls</p>
          <h2>Admin dashboard</h2>
          <p>View registered users and add test resources without changing their password or signing into their account.</p>
        </div>
        <button className="secondary" onClick={() => void refresh()} disabled={loading}>{loading ? 'Loading…' : 'Refresh users'}</button>
      </div>

      {error && <p className="auth-error">{error}</p>}
      {message && <p className="status-message">{message}</p>}

      <div className="admin-user-list">
        {users.map((entry) => {
          const busy = busyUserId === entry.id
          return (
            <article className="admin-user-card" key={entry.id}>
              <div className="admin-user-title">
                <div>
                  <strong>{entry.email}</strong>
                  <span>{entry.isAdmin ? 'Administrator' : 'User'} · joined {formatDate(entry.createdAt)}</span>
                </div>
                <span className="admin-updated">{formatDate(entry.progressUpdatedAt)}</span>
              </div>

              <div className="admin-resource-grid">
                <span><b>{entry.feed}</b> Feed</span>
                <span><b>{entry.tickets}</b> Tickets</span>
                <span><b>{entry.discoveryMarks}</b> Marks</span>
                <span><b>{entry.sessions}</b> Sessions</span>
                <span><b>{entry.fish}</b> Fish</span>
                <span><b>{entry.inventory}</b> Items</span>
              </div>

              <div className="admin-actions">
                <button className="secondary" disabled={busy || !entry.progressUpdatedAt} onClick={() => void grant(entry.id, { feed: 10 }, '+10 feed')}>+10 Feed</button>
                <button className="secondary" disabled={busy || !entry.progressUpdatedAt} onClick={() => void grant(entry.id, { tickets: 1 }, '+1 ticket')}>+1 Ticket</button>
                <button className="secondary" disabled={busy || !entry.progressUpdatedAt} onClick={() => void grant(entry.id, { discoveryMarks: 1 }, '+1 discovery mark')}>+1 Mark</button>
                <button className="primary" disabled={busy || !entry.progressUpdatedAt} onClick={() => void grant(entry.id, { feed: 50, tickets: 5, discoveryMarks: 5 }, 'Test bundle')}>{busy ? 'Granting…' : 'Grant test bundle'}</button>
              </div>
              {!entry.progressUpdatedAt && <small className="admin-note">This account must open ThriveCove once before resources can be granted.</small>}
            </article>
          )
        })}
        {!loading && users.length === 0 && <p className="muted">No users found.</p>}
      </div>
    </section>
  )
}
